import { Order as _Order } from '@team-milva/lab-models/resources'
import {
  isDeliveryDelivered,
  isTatGroupFullyCancelled,
} from '@team-milva/lab-utils/orderDeliveries'
import type { TInvoiceSyncStatus } from '@team-milva/lab-models/invoice'
import type { TStatusChipVariant } from '@team-milva/lab-components'
import { t } from '@/locales'
import { getOrderInvoiceBlockReasons } from '@team-milva/lab-utils/orderInvoiceBlocking'

export class Order extends _Order {
  constructor(params: Partial<_Order> = {}) {
    super(params)
  }

  /**
   * @param invoiceSyncStatus The order's invoice's `statuses.sync`, resolved via
   *   `getOrderInvoiceSyncStatus()`. Passed in rather than looked up because the model has no
   *   store access — the same shape `getBcInvoiceStatusChip()` uses, so the order chip and the
   *   invoice chip can't drift apart. Omitted where an invoicing failure isn't relevant.
   */
  getStatusChip(invoiceSyncStatus?: TInvoiceSyncStatus): {
    variant: TStatusChipVariant
    label: string
  } {
    const { orderStatus, sampleRegistrationStatus, paymentStatus } =
      this.statuses

    if (orderStatus === 'cancelled')
      return {
        variant: 'muted',
        label: t('area.order.status.common.cancelled'),
      }
    if (orderStatus === 'completed')
      return {
        variant: 'green',
        label: t('area.order.status.order.completed'),
      }
    // Outranks both payment-status branches below: 'ready-to-charge' and 'invoice-sent' are
    // the two green states a failed sync directly contradicts, and without this the order
    // reads as fine on every list. Only 'failed' and 'posted-not-sent' surface here — 'in-progress' is
    // invoice progress, not order lifecycle, and stay on the invoicing surfaces.
    if (invoiceSyncStatus === 'failed')
      return { variant: 'red', label: t('W: Fakturering fejlet') }
    // Posted in BC but never confirmed sent — reads as neither failed nor sent.
    if (invoiceSyncStatus === 'posted-not-sent')
      return { variant: 'amber', label: t('W: Bogført, ikke sendt') }
    if (paymentStatus === 'ready-to-charge') {
      // A block reason overrides the otherwise-green "ready to invoice" chip — the order is
      // structurally ready (results are all in) but can't actually be sent yet.
      const blockReasons = getOrderInvoiceBlockReasons(this)
      if (blockReasons.length > 0) {
        return {
          variant: 'red',
          // Short on purpose — the reasons are listed in the order's Faktura tab.
          label: t('W: Fakturering fejlet'),
        }
      }
      return {
        variant: 'green',
        label: t('area.order.status.order.invoice-ready'),
      }
    }
    if (paymentStatus === 'invoice-sent')
      return {
        variant: 'green',
        label: t('area.order.status.order.invoice-sent'),
      }
    if (sampleRegistrationStatus === 'handed-over') {
      // A fully cancelled delivery will never go out, so it is not part of the "x/y" — the same
      // rule the order's own report status follows (MIL-413).
      const tatGroupValues = Object.entries(this.tatGroups ?? {})
        .filter(
          ([tatGroupID]) => !isTatGroupFullyCancelled(tatGroupID, this.samples)
        )
        .map(([, tatGroup]) => tatGroup)
      const total = tatGroupValues.length
      const sent = tatGroupValues.filter((x) =>
        isDeliveryDelivered(x.statuses.labReportStatus)
      ).length

      // A failed send needs someone to act, and nothing else on the list would say so: the plain
      // "x/y leveringer" count looks the same as a delivery still in progress (MIL-367).
      //
      // Approved and scheduled deliveries deliberately do NOT get a chip here. The order list stays
      // a simple count of how many deliveries have gone out; the detailed stages live on the
      // deliveries list and on each delivery (decided with MM, 2026-10-01).
      const hasFailedDelivery = tatGroupValues.some(
        (x) => x.statuses.labReportStatus === 'sent-failed'
      )
      if (hasFailedDelivery)
        return { variant: 'red', label: t('W: Afsendelse fejlet') }

      return {
        variant: 'amber',
        label: t('area.order.status.order.deliveries-count', {
          sent,
          total,
          count: total,
        }),
      }
    }

    if (sampleRegistrationStatus === 'awaiting-drop-off')
      return {
        variant: 'neutral',
        label: t('area.order.status.common.awaiting-drop-off'),
      }

    if (sampleRegistrationStatus === 'handed-over-partially') {
      const nonCancelledSamples = this.samples.filter(
        (x) =>
          x.analyses.length === 0 ||
          !x.analyses.every((a) => a.timestamps.cancelledAt !== null)
      )
      const registeredCount = nonCancelledSamples.filter(
        (x) => x.timestamps.registeredAt !== null
      ).length
      const total = nonCancelledSamples.length

      return {
        variant: 'blue',
        label: t('area.order.status.common.samples-received-count', {
          x: registeredCount,
          y: total,
          count: total,
        }),
      }
    }

    const remainingUnregisteredSamples = this.samples.filter(
      (x) =>
        x.timestamps.registeredAt === null &&
        (x.analyses.length === 0 ||
          !x.analyses.every((a) => a.timestamps.cancelledAt !== null))
    )
    if (
      remainingUnregisteredSamples.length > 0 &&
      remainingUnregisteredSamples.every((x) => x.forwarding !== null)
    ) {
      return { variant: 'blue', label: t('W: Videresendt') }
    }

    return { variant: 'blue', label: t('area.order.status.common.in-transit') }
  }

  getPriorityLabel(): string {
    if (this.priority === 'high') return t('W: Høj')
    if (this.priority === 'low') return t('W: Lav')
    return t('W: Middel')
  }
}
